import { useQuery } from "@tanstack/react-query";
import { MarketTag } from "../../components/MarketTag";
import { MarketTypeFilter } from "../../components/MarketTypeFilter";
import type { MarketTypeChoice } from "../../components/marketType";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  INDICATOR_CATALOG,
  INDICATOR_GROUPS,
  INDICATOR_KINDS,
  catalogEntry,
  formatRef,
  parseRef,
} from "@strat/format";
import type { IndicatorKind } from "@strat/format";

import { api } from "../../api";
import { ActivityItem } from "../../components/Activity";
import { PagedList } from "../../components/PagedList";
import type { ActivityRow } from "../../components/Activity";
import { StrategyBadge } from "../../components/Badges";
import { RefChip } from "../../components/RefChip";
import { SortableTable } from "../../components/SortableTable";
import type { Column } from "../../components/SortableTable";
import { CardCollection, ViewToggle, useViewMode } from "../../components/ViewToggle";
import { AuthorChip, Disclaimer, Stars } from "../../components/social";
import { dd, pct, timeAgo, tone } from "../../format";
import { useSession } from "../../session";
import { Card, EmptyState, PageHead } from "../../shell/ui";
import { directionColumn } from "../../components/direction";

interface ExploreStrategy {
  id: string;
  name: string;
  description: string;
  indicator_kinds: string[];
  latest_version: number;
  rating_avg: number;
  rating_count: number;
  comment_count: number;
  fork_count: number;
  follower_count: number;
  slug: string;
  published_at: string;
  return_pct: number | null;
  drawdown_pct: number | null;
  market: string | null;
  market_type?: string | null;
  leverage?: number;
  direction?: "long" | "short";
  author_username: string;
  author_name: string;
  author_avatar: string;
  author_simulated: boolean;
  author_badge: "verified" | null;
  badge: "official" | "verified" | null;
}

const SORTS = [
  ["trending", "Trending"],
  ["top", "Top rated"],
  ["new", "Newest"],
  ["forks", "Most forked"],
  ["return", "Best backtest"],
] as const;

/** Short name for an indicator kind, tolerating kinds this build doesn't know. */
const shortName = (kind: string) =>
  (INDICATOR_KINDS as readonly string[]).includes(kind)
    ? catalogEntry(kind as IndicatorKind).short
    : kind.toUpperCase();

export function ExplorePage() {
  const [params, setParams] = useSearchParams();
  const sort = params.get("sort") ?? "trending";
  const kind = params.get("kind") ?? "";
  const { user: me } = useSession();
  const isAdmin = me?.role === "admin";
  const real = isAdmin && params.get("real") === "1";
  const badged = params.get("badge") === "1";
  const market = (params.get("market") ?? "all") as MarketTypeChoice;
  const [q, setQ] = useState(params.get("q") ?? "");
  const navigate = useNavigate();
  const [view, setView] = useViewMode("explore", "grid");
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const list = useQuery({
    queryKey: ["explore", sort, kind, params.get("q") ?? "", real, badged, market],
    queryFn: () =>
      api<{ items: ExploreStrategy[]; total: number }>(
        `/hub/explore/strategies?sort=${sort}&kind=${kind}&q=${encodeURIComponent(params.get("q") ?? "")}${real ? "&real=1" : ""}${badged ? "&badge=1" : ""}${market !== "all" ? `&market=${market}` : ""}&limit=48`,
      ),
    placeholderData: (prev) => prev,
  });
  const feed = useQuery({
    queryKey: ["feed", "all"],
    queryFn: () => api<{ items: ActivityRow[] }>("/hub/feed?limit=25"),
    refetchInterval: 20_000,
  });

  return (
    <div className="explore">
      <div className="stack">
        <PageHead title="Explore strategies">
          <div className="row">
            <span className="muted">{list.data ? `${list.data.total} published` : ""}</span>
            <ViewToggle value={view} onChange={setView} />
          </div>
        </PageHead>
        <div className="filter-row">
          <div className="segmented segmented--wrap" role="group" aria-label="Sort">
            {SORTS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={sort === k ? "is-active" : undefined}
                onClick={() => set("sort", k === "trending" ? "" : k)}
              >
                {label}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              // A pasted reference (alex/rsi-dip:v2@sha256:…) opens that strategy directly.
              const ref = q.includes("/") ? parseRef(q) : null;
              if (ref) {
                navigate(`/s/${formatRef(ref)}`);
                return;
              }
              set("q", q.trim());
            }}
          >
            <input
              type="search"
              placeholder="Search, or paste alex/name:v1"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search strategies"
            />
          </form>
          <MarketTypeFilter value={market} onChange={(m) => set("market", m === "all" ? "" : m)} stocks />
          <select value={kind} onChange={(e) => set("kind", e.target.value)} aria-label="Indicator">
            <option value="">Any indicator</option>
            {INDICATOR_GROUPS.map((g) => (
              <optgroup key={g} label={g}>
                {INDICATOR_CATALOG.filter((e) => e.group === g).map((e) => (
                  <option key={e.kind} value={e.kind}>
                    {e.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <label className="checkbox" title="Only strategies with an Official or Verified badge">
            <input
              type="checkbox"
              checked={badged}
              onChange={(e) => set("badge", e.target.checked ? "1" : "")}
            />
            Verified &amp; official
          </label>
          {isAdmin && (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={real}
                onChange={(e) => set("real", e.target.checked ? "1" : "")}
              />
              Real members only
            </label>
          )}
        </div>
        {view === "details" ? (
          <Card flush>
            <SortableTable
              tableId="explore-strategies"
              rows={list.data?.items ?? []}
              loading={list.isPending}
              rowKey={(s) => s.id}
              onRowClick={(s) => navigate(`/s/${s.id}`)}
              columns={STRATEGY_COLUMNS}
              caption="Published strategies"
            />
          </Card>
        ) : (
          <CardCollection mode={view} busy={list.isFetching}>
            {list.data?.items.map((s) => (
              <StrategyCard key={s.id} s={s} />
            ))}
          </CardCollection>
        )}
        {list.data?.items.length === 0 && (
          <Card>
            <EmptyState icon="◇" title="Nothing matches">
              Try another search, or clear the filters.
            </EmptyState>
          </Card>
        )}
        <Disclaimer />
      </div>
      <aside className="explore__side">
        <Card title="Latest activity" flush>
          <PagedList
            className="activity-list"
            items={feed.data?.items ?? []}
            pageSize={10}
            render={(a) => <ActivityItem key={a.id} a={a} compact />}
          />
        </Card>
      </aside>
    </div>
  );
}

/** One published strategy as a card: the same element in grid and list view. */
function StrategyCard({ s }: { s: ExploreStrategy }) {
  return (
    <Link to={`/s/${s.id}`} className="card strategy-card">
      <div className="card__body">
        <div className="strategy-card__head">
          <h2>{s.name}</h2>
          {s.badge && <StrategyBadge level={s.badge} />}
          <span className="badge">v{s.latest_version}</span>
        </div>
        <RefChip
          namespace={s.author_username}
          slug={s.slug}
          version={s.latest_version}
          compact
          link={false}
        />
        <AuthorChip
          username={s.author_username}
          name={s.author_name}
          avatar={s.author_avatar}
          simulated={s.author_simulated}
          badge={s.author_badge}
          suffix={<span className="muted small">· {timeAgo(s.published_at)}</span>}
        />
        {s.description && (
          <p className="strategy-card__desc muted collection-hide-in-list">{s.description}</p>
        )}
        <div className="strategy-card__stats collection-numbers">
          <span>
            <span className="stat__label">Backtest</span>
            <strong className={tone(s.return_pct)}>{pct(s.return_pct)}</strong>
          </span>
          <span>
            <span className="stat__label">Max DD</span>
            <strong>{dd(s.drawdown_pct, 1)}</strong>
          </span>
          <span>
            <span className="stat__label">Market</span>
            <strong className="mono small">
              {s.market ?? "—"} <MarketTag marketType={s.market_type} leverage={s.leverage} />
            </strong>
          </span>
        </div>
        <div className="strategy-card__foot collection-aside">
          <Stars value={s.rating_avg} count={s.rating_count} size="sm" />
          <span className="muted small">
            <span title="comments">💬 {s.comment_count}</span> · <span title="forks">⑂ {s.fork_count}</span> ·{" "}
            <span title="followers">👁 {s.follower_count}</span>
          </span>
        </div>
        <div className="strategy-card__chips collection-hide-in-list">
          {s.indicator_kinds.map((k) => (
            <span key={k} className="chip">
              {shortName(k)}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}

/** The details view: every number in a column, each one sortable. */
const STRATEGY_COLUMNS: Column<ExploreStrategy>[] = [
  {
    key: "name",
    label: "Strategy",
    render: (s) => (
      <span className="collection-name">
        <strong>{s.name}</strong>
        {s.badge && <StrategyBadge level={s.badge} />}
        <RefChip
          namespace={s.author_username}
          slug={s.slug}
          version={s.latest_version}
          compact
          link={false}
        />
      </span>
    ),
    sortValue: (s) => s.name,
    firstDirection: "asc",
  },
  {
    key: "by",
    label: "By",
    render: (s) => (
      <AuthorChip
        username={s.author_username}
        name={s.author_name}
        avatar={s.author_avatar}
        simulated={s.author_simulated}
        badge={s.author_badge}
      />
    ),
    sortValue: (s) => s.author_name,
    firstDirection: "asc",
  },
  {
    key: "market",
    label: "Market",
    render: (s) => (
      <span className="mono small">
        {s.market ?? "—"} <MarketTag marketType={s.market_type} leverage={s.leverage} />
      </span>
    ),
    sortValue: (s) => s.market,
    firstDirection: "asc",
  },
  directionColumn(),
  {
    key: "ret",
    label: "Backtest",
    align: "right",
    render: (s) => <span className={tone(s.return_pct)}>{pct(s.return_pct)}</span>,
    sortValue: (s) => s.return_pct,
  },
  {
    key: "dd",
    label: "Max DD",
    align: "right",
    render: (s) => dd(s.drawdown_pct, 1),
    sortValue: (s) => s.drawdown_pct,
    firstDirection: "asc",
  },
  {
    key: "rating",
    label: "Rating",
    render: (s) => <Stars value={s.rating_avg} count={s.rating_count} size="sm" />,
    sortValue: (s) => (s.rating_count ? s.rating_avg : null),
  },
  {
    key: "comments",
    label: "Comments",
    align: "right",
    render: (s) => s.comment_count,
    sortValue: (s) => s.comment_count,
  },
  {
    key: "forks",
    label: "Forks",
    align: "right",
    render: (s) => s.fork_count,
    sortValue: (s) => s.fork_count,
  },
  {
    key: "followers",
    label: "Followers",
    align: "right",
    render: (s) => s.follower_count,
    sortValue: (s) => s.follower_count,
  },
  {
    key: "indicators",
    label: "Indicators",
    render: (s) => <span className="small">{s.indicator_kinds.map(shortName).join(", ")}</span>,
  },
  {
    key: "when",
    label: "Published",
    align: "right",
    render: (s) => timeAgo(s.published_at),
    sortValue: (s) => new Date(s.published_at).getTime(),
  },
];
